import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Zap, Play, ArrowRight, AlertTriangle, Loader2, Banknote, Gauge } from "lucide-react";
import { toast } from "sonner";

const CURRENCIES = ["GBP", "USD", "EUR"];
const fmt = (v, cur) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: cur, notation: "compact", maximumFractionDigits: 1 }).format(v || 0);

const TYPES = [
  { key: "supplier", label: "Tier-1 supplier" },
  { key: "country", label: "Country / region" },
  { key: "theme", label: "Risk theme" },
  { key: "geo", label: "Geopolitical" },
];

const GEO_KINDS = [
  { key: "dyad", label: "Bilateral tension" },
  { key: "chokepoint", label: "Trade chokepoint" },
  { key: "material", label: "Critical material" },
  { key: "visa", label: "Labour mobility" },
];

export const ScenarioSimulator = () => {
  const [options, setOptions] = useState(null);
  const [type, setType] = useState("country");
  const [target, setTarget] = useState("");
  const [geoKind, setGeoKind] = useState("chokepoint");
  const [severity, setSeverity] = useState(0.7);
  const [currency, setCurrency] = useState("GBP");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/scenario/options").then(({ data }) => setOptions(data)).catch(() => {});
  }, []);

  const targetList =
    type === "supplier" ? (options?.suppliers || []).map((s) => ({ id: s.supplier_id, label: `${s.legal_name} (${s.country})` }))
      : type === "country" ? (options?.countries || []).map((c) => ({ id: c, label: c }))
        : type === "geo" ? (options?.geo?.[geoKind] || []).map((g) => ({ id: g.id, label: g.label }))
          : (options?.themes || []).map((t) => ({ id: t.theme_id, label: t.name }));

  // Effective target falls back to the first option when the current one isn't
  // in the active list (e.g. after switching shock type) — no reset effect needed.
  const effectiveTarget = targetList.some((t) => t.id === target) ? target : targetList[0]?.id || "";

  const run = async () => {
    if (!effectiveTarget) return;
    setLoading(true);
    try {
      const shock =
        type === "geo"
          ? { type: "geo", geo: { shock_type: geoKind, target: effectiveTarget }, severity }
          : { type, target_ids: [effectiveTarget], severity };
      const { data } = await api.post("/scenario/simulate", { shock, currency });
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Simulation failed — please try again.");
    } finally {
      setLoading(false);
    }
  };

  const posTone = (d) => (d > 0 ? "text-red-600" : d < 0 ? "text-emerald-600" : "text-ek-ink/65");

  return (
    <section className="rounded-md border border-ek-purple/20 bg-gradient-to-br from-ek-purple/[0.06] to-transparent p-6" data-testid="scenario-simulator">
      <h2 className="flex items-center gap-2 font-display text-xl font-bold text-ek-ink">
        <Zap size={18} className="text-ek-purple" /> Scenario simulator
      </h2>
      <p className="mt-1 text-sm text-ek-ink/65">
        Shock a supplier, region, theme or geopolitical lever and watch it cascade through the n-tier graph — re-pricing financial exposure and posture in real time.
      </p>

      <div className="mt-5 grid gap-5 lg:grid-cols-5">
        {/* Controls */}
        <div className="space-y-4 lg:col-span-2">
          <div className="flex gap-1 rounded-md border border-black/10 bg-white p-0.5">
            {TYPES.map((t) => (
              <button
                key={t.key}
                onClick={() => { setType(t.key); setResult(null); }}
                data-testid={`scenario-type-${t.key}`}
                className={`flex-1 rounded px-2 py-1.5 text-xs font-semibold transition-colors ${
                  type === t.key ? "bg-ek-ink text-white" : "text-ek-ink/65 hover:text-ek-ink"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {type === "geo" && (
            <div>
              <label className="mb-1 block text-xs font-medium text-ek-ink/65">Geopolitical lever</label>
              <div className="grid grid-cols-2 gap-1 rounded-md border border-black/10 bg-white p-0.5">
                {GEO_KINDS.map((g) => (
                  <button
                    key={g.key}
                    onClick={() => { setGeoKind(g.key); setResult(null); }}
                    data-testid={`scenario-geokind-${g.key}`}
                    className={`rounded px-2 py-1.5 text-xs font-semibold transition-colors ${
                      geoKind === g.key ? "bg-ek-purple text-white" : "text-ek-ink/65 hover:text-ek-ink"
                    }`}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-medium text-ek-ink/65">Target</label>
            <select
              value={effectiveTarget}
              onChange={(e) => setTarget(e.target.value)}
              data-testid="scenario-target-select"
              className="w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm text-ek-ink outline-none focus:border-ek-purple"
            >
              {targetList.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs font-medium text-ek-ink/65">Shock severity</label>
              <span className="text-xs font-bold text-ek-purple" data-testid="scenario-severity-value">{Math.round(severity * 100)}%</span>
            </div>
            <input
              type="range" min="0.1" max="1" step="0.05" value={severity}
              onChange={(e) => setSeverity(parseFloat(e.target.value))}
              data-testid="scenario-severity-slider"
              className="w-full accent-ek-purple"
            />
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="flex gap-1 rounded-md border border-black/10 bg-white p-0.5">
              {CURRENCIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCurrency(c)}
                  data-testid={`scenario-currency-${c}`}
                  className={`rounded px-2.5 py-1 text-xs font-semibold ${currency === c ? "bg-ek-ink text-white" : "text-ek-ink/65"}`}
                >
                  {c}
                </button>
              ))}
            </div>
            <button
              onClick={run}
              disabled={loading || !effectiveTarget}
              data-testid="scenario-run-button"
              className="ek-btn flex items-center gap-1.5 !px-5 disabled:opacity-50"
            >
              {loading ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} Run simulation
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-3">
          {!result ? (
            <div className="flex h-full min-h-[220px] items-center justify-center rounded-md border border-dashed border-black/10 bg-white/50 text-sm text-ek-ink/65" data-testid="scenario-empty">
              Configure a shock and run the simulation.
            </div>
          ) : (
            <div className="space-y-4" data-testid="scenario-result">
              {/* Headline */}
              <div className="rounded-md border border-black/5 bg-white p-5">
                <p className="text-xs font-medium uppercase tracking-wide text-ek-ink/65">Headline financial impact</p>
                <p className="mt-1 font-display text-4xl font-bold text-red-600" data-testid="scenario-headline">
                  {fmt(result.headline_financial_impact, result.currency)}
                </p>
                <div className="mt-3 flex flex-wrap gap-4 border-t border-black/5 pt-3 text-sm">
                  <Metric icon={Gauge} label="Risk posture">
                    <span className="font-semibold">{result.posture.baseline}</span>
                    <ArrowRight size={12} className="text-ek-ink/30" />
                    <span className={`font-bold ${posTone(result.posture.delta)}`}>{result.posture.scenario}</span>
                  </Metric>
                  <Metric icon={Banknote} label="Portfolio ALE">
                    <span className="font-semibold">{fmt(result.fair.baseline_ale, result.currency)}</span>
                    <ArrowRight size={12} className="text-ek-ink/30" />
                    <span className="font-bold text-red-600">{fmt(result.fair.scenario_ale, result.currency)}</span>
                  </Metric>
                  <Metric icon={AlertTriangle} label="Services breached">
                    <span className="font-bold text-red-600">{result.breached_services}</span>
                    <span className="text-ek-ink/65">/ {result.impacted_services.length} hit</span>
                  </Metric>
                </div>
                <p className="mt-2 text-xs text-ek-ink/65">
                  {result.geo && (
                    <span className="mr-1 rounded bg-ek-purple/10 px-1.5 py-0.5 font-semibold text-ek-purple" data-testid="scenario-geo-tag">
                      Geo · {result.geo.shock_type} · {result.geo.directly_hit.length} nodes on route
                    </span>
                  )}
                  {result.impacted_supplier_count} suppliers and {result.affected_tier1.length} tier-1 nodes impacted across the n-tier graph.
                </p>
              </div>

              {/* Breached services */}
              <div className="rounded-md border border-black/5 bg-white p-5">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-ek-ink/65">Impacted business services</p>
                <div className="space-y-1.5">
                  {result.impacted_services.map((s) => (
                    <div key={s.service_id} className="flex items-center justify-between gap-2 text-sm" data-testid={`scenario-service-${s.service_id}`}>
                      <span className="flex items-center gap-2 truncate">
                        {s.breach ? (
                          <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700">BREACH</span>
                        ) : (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">HIT</span>
                        )}
                        <span className="truncate text-ek-ink/80">{s.name}</span>
                        <span className="text-xs text-ek-ink/65">{s.outage_hours}h / {s.impact_tolerance_hours}h tol</span>
                      </span>
                      <span className="shrink-0 font-semibold text-ek-ink">{fmt(s.financial_impact, result.currency)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const Metric = ({ icon: Icon, label, children }) => (
  <div className="flex items-center gap-2">
    <Icon size={14} className="text-ek-ink/35" />
    <span className="text-xs text-ek-ink/65">{label}</span>
    <span className="flex items-center gap-1">{children}</span>
  </div>
);
